// Task status definitions, labels, and colour maps for the My Team feature.
// Single source of truth — import from here, never redefine.

export const TASK_STATUSES = ['todo', 'in_progress', 'blocked', 'done', 'cancelled'];

export const TASK_STATUS_LABELS = {
  todo: 'To Do',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  done: 'Done',
  cancelled: 'Cancelled',
};

export const TASK_STATUS_COLORS = {
  todo: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  blocked: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  done: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};

/**
 * THE task STATUS TABS (owner ruling 2026-10-02, My Tasks): `All · To do ·
 * In progress · Blocked · Done`, with **Done holding `cancelled` too** — five
 * tabs over five statuses, so a cancelled task is never in no tab. The labels
 * are the owner's sentence case and are deliberately NOT `TASK_STATUS_LABELS`
 * (the badges on Teams/Kanban keep "To Do"); the GAG tabs are a different list
 * (`GAG_STATUS_TABS` in `constants/gagBreakdown.js`) — import that, never copy.
 * 🔑 Counts and rows come from the SAME predicate (`taskStatusTabOf`), so
 * `Done (3)` is exactly the rows the Done tab shows.
 */
export const TASK_STATUS_TAB_ALL = 'all';
export const TASK_STATUS_TABS = [
  { id: TASK_STATUS_TAB_ALL, label: 'All' },
  { id: 'todo', label: 'To do' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'blocked', label: 'Blocked' },
  { id: 'done', label: 'Done' },
];

/** The status tab one task belongs to (`cancelled` → `done`; unknown → none). */
export function taskStatusTabOf(status) {
  if (status === 'cancelled') return 'done';
  return TASK_STATUS_TABS.some((t) => t.id === status && t.id !== TASK_STATUS_TAB_ALL) ? status : null;
}

/** The rows one task status tab shows. An unknown id shows nothing, never everything. */
export function tasksInStatusTab(tasks, tab) {
  if (tab === TASK_STATUS_TAB_ALL) return tasks;
  return tasks.filter((t) => taskStatusTabOf(t.status) === tab);
}

/** `{ all, todo, in_progress, blocked, done }` over one row set; `0` included. */
export function taskStatusTabCounts(tasks) {
  const counts = {};
  TASK_STATUS_TABS.forEach((t) => { counts[t.id] = 0; });
  counts[TASK_STATUS_TAB_ALL] = tasks.length;
  tasks.forEach((t) => {
    const tab = taskStatusTabOf(t.status);
    if (tab) counts[tab] += 1;
  });
  return counts;
}

export const TASK_STATUS_COLORS_HEX = {
  todo: '#6B7280',
  in_progress: '#3B82F6',
  blocked: '#EF4444',
  done: '#10B981',
  cancelled: '#9CA3AF',
};

export const TASK_PRIORITIES = ['urgent', 'high', 'medium', 'low'];

export const TASK_PRIORITY_LABELS = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export const TASK_PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
};

export const TASK_PRIORITY_WEIGHT = {
  urgent: 1,
  high: 2,
  medium: 3,
  low: 4,
};

// Status icons for Gantt bars
export const TASK_STATUS_ICONS = {
  todo: '○',
  in_progress: '◉',
  blocked: '⊘',
  done: '✓',
};

// Icon colours (for use on team-coloured bars where white text is default)
export const TASK_STATUS_ICON_COLORS = {
  todo: '#d1d5db',      // gray-300
  in_progress: '#93c5fd', // blue-300
  blocked: '#ff4444',    // vivid red
  done: '#86efac',       // green-300
};

// Kanban column definitions
export const KANBAN_COLUMNS = [
  { key: 'todo', label: 'To Do', color: 'gray' },
  { key: 'blocked', label: 'Blocked', color: 'red' },
  { key: 'in_progress', label: 'In Progress', color: 'blue' },
  { key: 'done', label: 'Done', color: 'green' },
];

// Team member role labels
export const TEAM_ROLE_LABELS = {
  personal: 'Personal',
  creator: 'Creator',
  lead: 'Lead',
  member: 'Member',
};

export const TEAM_ROLE_COLORS = {
  personal: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  creator: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  lead: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  member: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

// Team member invite statuses
export const TEAM_MEMBER_STATUS_LABELS = {
  pending: 'Pending',
  accepted: 'Active',
};

export const TEAM_MEMBER_STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  accepted: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
};

// Gag Breakdown (SFX gags) — statuses share the task values plus the
// gag-specific 'ready' ("Ready to shoot") sign-off state. Keep GAG_* separate
// from TASK_* — tasks never have a 'ready' status.
// Iterate GAG_STATUSES for ordered pill/option lists — never Object.keys of
// GAG_STATUS_LABELS / GAG_STATUS_COLORS (their spread ordering puts 'ready' last).
export const GAG_STATUSES = ['todo', 'in_progress', 'blocked', 'ready', 'done', 'cancelled'];

// 🔀 SENTENCE CASE, AND NO LONGER A SPREAD OF TASK_STATUS_LABELS (owner ruling
// 2026-10-02): the Breakdown's status tabs read "Not started · In progress ·
// Blocked · Ready to shoot · Done · Cancelled", and every gag status BADGE must
// say the same words as the tab it sits under. Tasks are NOT gags and keep
// TASK_STATUS_LABELS ("To Do", "In Progress") unchanged — so this map is
// written out in full rather than spread, which also makes its key order
// GAG_STATUSES order. Still iterate GAG_STATUSES for ordered lists (above).
export const GAG_STATUS_LABELS = {
  todo: 'Not started',
  in_progress: 'In progress',
  blocked: 'Blocked',
  ready: 'Ready to shoot',
  done: 'Done',
  cancelled: 'Cancelled',
};

// Chart-style hex colours for gag bars (department Timeline view) — the hex
// counterpart of colors.js GAG_STATUS_COLORS, mirroring its badge hues:
// 'ready' is emerald and 'done' green (distinct from TASK_STATUS_COLORS_HEX,
// where done is the emerald hue and 'ready' doesn't exist).
export const GAG_STATUS_COLORS_HEX = {
  todo: '#6B7280',
  in_progress: '#3B82F6',
  blocked: '#EF4444',
  ready: '#10B981',
  done: '#22C55E',
  cancelled: '#9CA3AF',
};

export const DAY_NIGHT_OPTIONS = [
  { value: 'day', label: 'Day' },
  { value: 'night', label: 'Night' },
  { value: 'dawn', label: 'Dawn' },
  { value: 'dusk', label: 'Dusk' },
];

export const INT_EXT_OPTIONS = [
  { value: 'int', label: 'INT' },
  { value: 'ext', label: 'EXT' },
];

// Value→label lookup maps derived from the OPTIONS arrays — import these
// instead of re-deriving Object.fromEntries maps locally.
export const DAY_NIGHT_LABELS = Object.fromEntries(DAY_NIGHT_OPTIONS.map((o) => [o.value, o.label]));
export const INT_EXT_LABELS = Object.fromEntries(INT_EXT_OPTIONS.map((o) => [o.value, o.label]));

// Team color palette for preset picker
export const TEAM_COLOR_PALETTE = [
  '#3B82F6', // blue
  '#10B981', // emerald
  '#8B5CF6', // purple
  '#F59E0B', // amber
  '#F43F5E', // rose
  '#06B6D4', // cyan
  '#F97316', // orange
  '#6366F1', // indigo
];
